import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { validateCliente } from '../utils/validators';
import { useAuth } from '../context/AuthContext';

const accesoRestringidoStyles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  card: { backgroundColor: 'white', padding: '2rem 3rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '500px' },
  icon: { fontSize: '4rem', display: 'block', marginBottom: '1rem' },
  message: { color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' },
  suggestion: { fontSize: '0.9rem', color: '#9ca3af', marginBottom: '2rem' },
  button: { backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' },
};

const Clientes = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [accesoDenegado, setAccesoDenegado] = useState(false);
  const [mensajeError, setMensajeError] = useState('');
  const [clientes, setClientes] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [limiteClientes, setLimiteClientes] = useState(null);
  const [infoPlan, setInfoPlan] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [formData, setFormData] = useState({
    nombres: '',
    apellidos: '',
    identificacion: '',
    whatsapp_number: '',
    email: ''
  });

  // Estilos comunes para inputs
  const inputStyle = {
    border: '2px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '1rem',
    backgroundColor: '#fafafa',
    width: '100%',
    transition: 'all 0.2s'
  };

  useEffect(() => {
    if (!authLoading) {
      if (user?.tenant?.acceso_bloqueado === true) {
        setAccesoDenegado(true);
        setMensajeError('Tu suscripción está suspendida. Contacta al administrador.');
        setLoading(false);
        return;
      }
      if (user?.rol !== 'admin' && user?.puede_ver_clientes === false) {
        setAccesoDenegado(true);
        setMensajeError('No tienes permisos para ver los clientes.');
        setLoading(false);
        return;
      }
      fetchClientes();
      fetchInfoPlan();
    }
  }, [authLoading]);

  useEffect(() => {
    // Filtrar clientes cuando cambia el término de búsqueda o la lista
    if (clientes.length > 0) {
      const filtrados = clientes.filter(c =>
        c.nombres.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.apellidos.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.identificacion.includes(searchTerm)
      );
      setClientesFiltrados(filtrados);
    } else {
      setClientesFiltrados([]);
    }
  }, [searchTerm, clientes]);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/clientes');
      setClientes(data);
      setClientesFiltrados(data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccesoDenegado(true);
        setMensajeError(error.response.data.message || 'No tienes acceso a esta sección.');
      } else {
        toast.error(error?.response?.data?.error || 'Error al cargar clientes');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInfoPlan = async () => {
    try {
      const { data } = await api.get('/tenant/plan-info');
      
      // Asegurar que max_clientes sea número
      const maxClientes = Number(data.plan?.max_clientes || data.max_clientes || 99);
      setLimiteClientes(maxClientes);
      setInfoPlan(data);
      
      // Mostrar advertencia si está cerca del límite
      if (maxClientes && clientes.length > 0) {
        const porcentaje = (clientes.length / maxClientes) * 100;
        if (porcentaje >= 80) {
          toast.warning(`Has alcanzado el ${Math.round(porcentaje)}% de tu límite de clientes (${clientes.length}/${maxClientes})`);
        }
      }
    } catch {
      setLimiteClientes(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateCliente(formData);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // Validar límite de clientes solo para creación (no para edición)
    if (!editingId && limiteClientes && clientes.length >= limiteClientes) {
      toast.error(`❌ Has alcanzado el límite de ${limiteClientes} clientes de tu plan. Actualiza tu plan para agregar más.`);
      return;
    }

    try {
      if (editingId) {
        await api.put(`/clientes/${editingId}`, formData);
        toast.success('Cliente actualizado');
      } else {
        const response = await api.post('/clientes', formData);
        const { data } = response;

        if (data.error) {
          toast.error(data.error);
          return;
        }

        toast.success(data.message || 'Cliente creado');
      }
      fetchClientes();
      fetchInfoPlan(); // Actualizar info del plan después de crear
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar cliente');
    }
  };

  const handleEdit = (cliente) => {
    setFormData(cliente);
    setEditingId(cliente.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirmAction({ onConfirm: async () => {
      try {
        await api.delete(`/clientes/${id}`);
        toast.success('Cliente eliminado');
        fetchClientes();
        fetchInfoPlan();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al eliminar');
      }
    }});
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ 
      nombres: '', 
      apellidos: '', 
      identificacion: '', 
      whatsapp_number: '', 
      email: '' 
    });
  };

  if (accesoDenegado) {
    return (
      <Layout>
        <div style={accesoRestringidoStyles.container}>
          <div style={accesoRestringidoStyles.card}>
            <span style={accesoRestringidoStyles.icon}>🔒</span>
            <h2>Acceso Restringido</h2>
            <p style={accesoRestringidoStyles.message}>{mensajeError}</p>
            <p style={accesoRestringidoStyles.suggestion}>
              Si crees que esto es un error, contacta al administrador del sistema para revisar los detalles de tu plan.
            </p>
            <button
              style={accesoRestringidoStyles.button}
              onClick={() => navigate('/')}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Volver al Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="text-center" style={{ padding: '3rem' }}>
          <div className="loading"></div>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>
            Cargando clientes...
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h2>👥 Clientes</h2>
          {limiteClientes && (
            <div style={{ 
              marginTop: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <div style={{ 
                fontSize: '0.875rem',
                color: 'var(--text-secondary)',
                background: 'var(--background)',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px'
              }}>
                📊 Clientes: {clientes.length} / {limiteClientes}
              </div>
              {limiteClientes && clientes.length >= limiteClientes && (
                <span style={{ 
                  fontSize: '0.875rem',
                  color: '#dc2626',
                  background: '#fee',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px'
                }}>
                  ⚠️ Límite alcanzado
                </span>
              )}
            </div>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
          disabled={user?.tenant?.acceso_bloqueado === true || (limiteClientes && clientes.length >= limiteClientes)}
          title={
            user?.tenant?.acceso_bloqueado === true
              ? 'Suscripción suspendida. Contacta al administrador.'
              : (limiteClientes && clientes.length >= limiteClientes ? 'Has alcanzado el límite de clientes de tu plan' : '')
          }
        >
          + Nuevo Cliente
        </button>
      </div>

      {/* Buscador */}
      <div className="card mb-3">
        <input
          type="text"
          className="form-input"
          placeholder="🔍 Buscar cliente por nombre, apellido o identificación..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={inputStyle}
          onFocus={(e) => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.6)';
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
            e.target.style.backgroundColor = '#fafafa';
            e.target.style.boxShadow = 'none';
          }}
        />
        {searchTerm && (
          <div style={{ 
            marginTop: '0.5rem', 
            fontSize: '0.875rem', 
            color: 'var(--text-secondary)',
            textAlign: 'right'
          }}>
            {clientesFiltrados.length} resultado(s) encontrado(s)
          </div>
        )}
      </div>

      <div className="card">
        {clientesFiltrados.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <span style={{ fontSize: '3rem' }}>👥</span>
            <h3 style={{ marginTop: '1rem' }}>
              {searchTerm ? 'No hay resultados para tu búsqueda' : 'No hay clientes creados'}
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              {searchTerm 
                ? 'Prueba con otros términos de búsqueda' 
                : 'Crea tu primer cliente haciendo click en el botón "+ Nuevo Cliente"'}
            </p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>C.I/Ruc</th>
                  <th>WhatsApp</th>
                  <th>Email</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientesFiltrados.map(cliente => (
                  <tr key={cliente.id}>
                    <td>{cliente.nombres} {cliente.apellidos}</td>
                    <td>{cliente.identificacion}</td>
                    <td>{cliente.whatsapp_number || '-'}</td>
                    <td>{cliente.email || '-'}</td>
                    <td>
                      {cliente.etapa_crm ? (
                        <span style={{
                          display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '999px',
                          fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
                          color: cliente.etapa_color || '#6b7280',
                          background: `${cliente.etapa_color || '#6b7280'}1a`,
                          border: `1px solid ${cliente.etapa_color || '#6b7280'}55`
                        }}>
                          {cliente.etapa_crm.toUpperCase()}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/clientes/${cliente.id}`)} title="Ver ficha completa">
                        Ficha
                      </button>
                      {(user?.rol === 'admin' || user?.puede_modificar_clientes) && (
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(cliente)}>
                          ✏️
                        </button>
                      )}
                      {(user?.rol === 'admin' || user?.puede_eliminar_clientes) && (
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(cliente.id)}>
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.5rem 1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  width: '42px', height: '42px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem',
                }}>
                  👥
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                    {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
                  </h3>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                    {editingId ? 'Modifica la información del cliente' : 'Completa los datos para agregar un nuevo cliente'}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '32px', height: '32px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ padding: '1.5rem 1.75rem', maxHeight: '70vh', overflowY: 'auto' }}>

                {/* Datos personales */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
                    <span style={{ background: '#eff6ff', color: '#2563eb', borderRadius: '6px', padding: '0.2rem 0.5rem', fontSize: '0.85rem', fontWeight: '700' }}>👤 Datos personales</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {[
                      { key: 'nombres',        label: 'Nombres',     required: true, placeholder: 'Ej. Juan Carlos' },
                      { key: 'apellidos',      label: 'Apellidos',   required: true, placeholder: 'Ej. Pérez López' },
                      { key: 'identificacion', label: 'Cédula / RUC', required: true, placeholder: '1234567890',
                        onChange: (e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 13); setFormData({...formData, identificacion: v}); } },
                      { key: 'whatsapp_number', label: 'WhatsApp', placeholder: '0999 999 999', type: 'tel',
                        onChange: (e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 15); setFormData({...formData, whatsapp_number: v}); } },
                      { key: 'email', label: 'Email', type: 'email', placeholder: 'correo@ejemplo.com' },
                    ].map(({ key, label, required, type, placeholder, onChange }) => (
                      <div key={key} className="form-group" style={key === 'email' ? { gridColumn: '1 / -1' } : {}}>
                        <label style={{ fontWeight: '600', color: '#374151', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.875rem' }}>
                          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
                        </label>
                        <input
                          type={type || 'text'}
                          className="form-input"
                          value={formData[key]}
                          onChange={onChange || ((e) => setFormData({...formData, [key]: e.target.value}))}
                          required={!!required}
                          placeholder={placeholder}
                          style={inputStyle}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.6)'; e.target.style.backgroundColor = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(59,130,246,0.3)'; e.target.style.backgroundColor = '#fafafa'; e.target.style.boxShadow = 'none'; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 1.75rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ minWidth: '120px' }}>
                  {editingId ? 'Guardar cambios' : '+ Crear cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Clientes;
